import { productUnits } from '@Arti-zen/package-backoffice'
import { number, object, string } from 'yup'

const numberSchema = number().transform((_, originalValue) => {
  const newValue = Number(String(originalValue).replace(',', '.'))
  if (originalValue === '' || Number.isNaN(newValue)) return null
  return newValue
})

const emptyStringToNull = (value: unknown) => {
  if (typeof value === 'string') {
    const v = value.trim()
    return v === '' ? null : v
  }

  if (typeof value === 'number' && Number.isNaN(value)) {
    return null
  }

  return value
}

const containsInvalidSymbol = (text: string): boolean => {
  const EMOJI_REGEX = /\p{Extended_Pictographic}/u
  const INVALID_TEXT_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\uFFFD\u200B\u200C\u200D\u2060\uFEFF]/

  return INVALID_TEXT_CHARACTERS.test(text) || EMOJI_REGEX.test(text)
}

export const cleanString = (text: string, multiline?: boolean) => {
  if (!text) return ''
  let cleaned = text
    .normalize('NFC')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .trim()

  if (!multiline) {
    cleaned = cleaned
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/[ \t]+/g, ' ')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n')
  }

  return cleaned
}

export const ProductBaseSchema = object({
  description: string()
    .required("Il n'y a pas de désignation (description)")
    .transform((value) => cleanString(value))
    .test(
      'unknow-symbol',
      () => 'Contient au moins un caractère invalide.',
      (value: string) => {
        const cleanedText = cleanString(value)
        return !containsInvalidSymbol(value) || cleanedText !== value
      }
    ),
  allDescription: string()
    .nullable()
    .notRequired()
    .transform((value) => cleanString(value))
    .test(
      'unknow-symbol',
      () => 'Contient au moins un caractère invalide.',
      (value: string) => {
        const cleanedText = cleanString(value, true)
        return !containsInvalidSymbol(value) || cleanedText !== value
      }
    ),
  price: numberSchema.typeError('Le prix doit être un nombre.').required("Il n'y a pas de prix facturé HT (price)"),
  netPrice: numberSchema.nullable().notRequired(),
  coef: numberSchema
    .notRequired()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  unit: string()
    .transform(emptyStringToNull)
    .notRequired()
    .nullable()
    .oneOf([...productUnits, null, '', ' '], ({ value }) => `L'unité renseignée "${value}" n'existe pas chez Artizen.`),
  vat: numberSchema
    .typeError('Le taux de TVA doit être un nombre.')
    .nullable()
    .test(
      'valid-vat',
      ({ value }) => `Le taux de TVA renseigné "${value}%" n'existe pas.`,
      (value) => value == null || [0, 5.5, 10, 20, 2.1, 8.5].includes(value)
    ),
}).test('netPrice-coef', "'Le calcul entre marge, prix facturé et coût est incorrect.", function (product) {
  const { price, netPrice, coef } = product

  if (netPrice === null && coef === null) return true

  const calculatedNetPrice = Math.round((price / (1 + coef / 100)) * 100) / 100
  if (netPrice !== calculatedNetPrice) {
    return this.createError({
      path: 'price',
      message: 'Le calcul entre marge, prix facturé et coût est incorrect.',
    })
  }

  return true
})
